/**
 * What a condition is allowed to ask about, and nothing more.
 *
 * A perk that only fires sometimes names a condition, and a condition is a predicate over this
 * record. Keeping the record small and flat is the point: it is the whole surface a perk can
 * react to, so widening it is a deliberate edit rather than a perk reaching into whatever state it
 * happened to find. Every field is derivable from a snapshot the mode already has.
 *
 * Nothing here is a wall clock and nothing here is a tick count, because a moment is what is true
 * now rather than how long it has been true. A perk that needs elapsed time is a status effect.
 */

export interface CharacterMoment {
  /** Moving under their own power, on the ground. Falling is not moving. */
  readonly moving: boolean;
  readonly aiming: boolean;
  readonly crouching: boolean;
  /** Off their feet, whether they can be picked up or not. */
  readonly downed: boolean;
  /** Current health over maximum, in [0, 1]. */
  readonly healthFraction: number;
  /** Rounds in the magazine over its capacity, in [0, 1]. One with no weapon. */
  readonly magazineFraction: number;
}

/**
 * A character standing still, upright and untouched. The identity every test starts from, and what
 * a surface with no live character to describe passes in.
 */
export const RESTING_MOMENT: CharacterMoment = {
  moving: false,
  aiming: false,
  crouching: false,
  downed: false,
  healthFraction: 1,
  magazineFraction: 1,
};
